import { Star, Quote, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import SEO from '@/components/ui-custom/SEO';

const Reviews = () => {
  const reviews = [
  {
    name: 'Kenneth Leach',
    location: 'Dothan, AL',
    rating: 5,
    text: "Robert was punctual and hooked up our water heater in a timely manner. He stood by his work to quickly fix a factory part leak. Very reasonable pricing!",
    service: 'Water Heater Installation',
  },
  {
    name: 'Susan Pearson',
    location: 'Ozark, AL',
    rating: 5,
    text: "They added electricity to my shed, including outlets and a light fixture. Very professional, did an outstanding job, and cost half of other estimates.",
    service: 'Electrical Installation',
  },
  {
    name: 'Bonnie Christian',
    location: 'Headland, AL',
    rating: 5,
    text: "Highly recommend! They are prompt, courteous, affordable, and provide high quality work. We have been quite satisfied with their plumbing services.",
    service: 'Plumbing Repair',
  },
  {
    name: 'Larry Davis',
    location: 'Midland City, AL',
    rating: 5,
    text: "Great bunch of guys. Very happy with the electrical work done and at a fair price. I would recommend them for any projects you may need.",
    service: 'Electrical Services',
  },
  {
    name: 'Scott Lowe',
    location: 'Enterprise, AL',
    rating: 5,
    text: "We were affected by Hurricane Michael. They came out the very same day we called and were a tremendous help.",
    service: 'Emergency Service',
  },
  {
    name: 'Pat Benefield',
    location: 'Geneva, AL',
    rating: 5,
    text: "Robert came out and installed my new water filter system the very same day. Great service.",
    service: 'Water Filter Installation',
  },
  {
    name: 'Vanessa Epperson',
    location: 'Newton, AL',
    rating: 5,
    text: "Awesome go-to company. They are true life savers!",
    service: 'General Plumbing',
  },
  {
    name: 'Cb Tech',
    location: 'Rehobeth, AL',
    rating: 5,
    text: "Called for a quote and they were very kind and professional.",
    service: 'Customer Service',
  },
];

  const platforms = [
    { name: 'Google', rating: '4.0', reviews: '20+' },
    { name: 'Yelp', rating: '5.0', reviews: '1+' },
    { name: 'Facebook', rating: '5.0', reviews: '1+' },
    { name: 'HomeAdvisor', rating: '4.9', reviews: '20+' },
  ];

  return (
    <>
      <SEO
        title="Customer Reviews | Ard's Plumbing & Electric Dothan AL"
        description="See what customers say about Ard's Plumbing & Electric. 5-star rated plumbing and electrical services in Dothan, AL. Read real reviews from real customers."
        keywords="Ard's Plumbing reviews, Dothan plumber reviews, electrician reviews Alabama, customer testimonials, 5 star plumber"
        canonical="/reviews"
        additionalSchema={{ '@context': 'https://schema.org', '@type': 'BreadcrumbList', 'itemListElement': [{ '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://ardsdothan.com/' }, { '@type': 'ListItem', 'position': 2, 'name': 'Reviews', 'item': 'https://ardsdothan.com/reviews' }] }}
      />

      {/* Hero Section */}
      <section className="bg-brand-blue pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 text-white text-sm px-4 py-2 rounded-full mb-6">
            <Star className="w-4 h-4 fill-white" />
            4.0+ Star Rated Service
          </div>
          <h1 className="font-heading font-black text-4xl lg:text-6xl text-white leading-tight mb-6">
            WHAT OUR<br />
            <span className="text-brand-yellow">CUSTOMERS SAY</span>
          </h1>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Don't just take our word for it. Here's what homeowners and businesses 
            throughout the Wiregrass area have to say about working with us.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              className="bg-brand-coral hover:bg-brand-coral/90 text-white rounded-full px-8 py-6 text-lg font-semibold"
            >
              <Link to="/contact-us">Join Our Happy Customers</Link>
            </Button>
            <a
              href="tel:3346925537"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full border-2 border-white text-white font-semibold hover:bg-white hover:text-brand-blue transition-colors"
            >
              <Phone className="w-5 h-5" />
              (334) 692-5537
            </a>
          </div>
        </div>
      </section>

      {/* Platform Ratings */}
      <section className="py-12 px-4 bg-brand-navy">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {platforms.map((platform, index) => (
              <div key={index} className="bg-white/10 rounded-2xl p-6 text-center">
                <p className="font-heading font-bold text-white text-lg mb-1">{platform.name}</p>
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Star className="w-5 h-5 text-brand-yellow fill-brand-yellow" />
                  <span className="font-bold text-white text-2xl">{platform.rating}</span>
                </div>
                <p className="text-white/70 text-sm">{platform.reviews} reviews</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading font-black text-3xl lg:text-5xl text-brand-black mb-4">
              CUSTOMER TESTIMONIALS
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Real reviews from real customers across the Wiregrass area.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-2xl p-6 hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-brand-yellow fill-brand-yellow" />
                  ))}
                </div>
                <Quote className="w-8 h-8 text-brand-blue/30 mb-3" />
                <p className="text-gray-700 mb-6">{review.text}</p>
                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div>
                    <p className="font-semibold text-brand-black">{review.name}</p>
                    <p className="text-sm text-gray-500">{review.location}</p>
                  </div>
                  <span className="text-xs bg-brand-blue/10 text-brand-blue px-3 py-1 rounded-full">
                    {review.service}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-brand-coral">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading font-black text-3xl lg:text-5xl text-white mb-6">
            EXPERIENCE THE DIFFERENCE
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Join hundreds of satisfied customers in Dothan and the Wiregrass area. 
            Contact Ard's Plumbing & Electric today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              className="bg-white text-brand-coral hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-semibold"
            >
              <Link to="/contact-us">Book Online</Link>
            </Button>
            <a
              href="tel:3346925537"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full border-2 border-white text-white font-semibold hover:bg-white hover:text-brand-coral transition-colors"
            >
              <Phone className="w-5 h-5" />
              Call (334) 692-5537
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default Reviews;
